// src/resources/dashboards/ProductEvolution/index.tsx
//
// Painel "Evolução do Produto" — carteira de redes (tela 1 de 2; o detalhe da
// rede vive em /dashboard-evolution/:chainId).
//
// Três coisas que a versão anterior desta tela errava e que aqui são regra:
//   1. Honestidade de estado. "Atualizado há X", spinner só quando o backend diz
//      que está calculando, e NUNCA barra de progresso falsa correndo sozinha.
//   2. Honestidade de recorte. Paginação real com contagem visível, em vez de
//      slice(0,10) silencioso.
//   3. Honestidade de fonte. Se o pré-agregado está defasado, a tela diz — não
//      mostra número velho como se fosse de hoje.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, MenuItem, TextField, Tooltip, Typography, useTheme,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import dayjs from 'dayjs';
import { Bar, BarChart, Legend, ResponsiveContainer, Tooltip as ReTooltip, XAxis, YAxis } from 'recharts';
import { useDataProvider, useNotify, usePermissions } from 'react-admin';
import { PERMISSIONS } from '../../../constants';
import { EVENTS, track } from '../../../lib/analytics';
import {
  COLORS, DeltaChip, ErrorCard, Section, SkeletonCard, cardSx, fmtBrl, fmtNum, useCountUp, useDashboardSection,
} from '../shared';
import PortfolioView from './PortfolioView';
import {
  ChainLifecycleStatus, OrderDir, PORTFOLIO_WINDOWS, PortfolioData, PortfolioOrderBy,
  PortfolioWindow, RISK_LABEL, RiskLevel, STATUS_LABEL,
} from './types';

// Humanizador pt-BR ("há X") — mesmo cálculo local do Painel 360; o plugin
// relativeTime do dayjs não é carregado globalmente e não vale puxar por isto.
const humanizeAgo = (iso?: string | null): string | null => {
  if (!iso) return null;
  const then = dayjs(iso);
  if (!then.isValid()) return null;
  const seconds = Math.max(0, dayjs().diff(then, 'second'));
  if (seconds < 60) return 'agora';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `há ${minutes} ${minutes === 1 ? 'minuto' : 'minutos'}`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} ${hours === 1 ? 'hora' : 'horas'}`;
  const days = Math.floor(hours / 24);
  return `há ${days} ${days === 1 ? 'dia' : 'dias'}`;
};

const STATUS_OPTIONS: ChainLifecycleStatus[] = ['ativa', 'em_risco', 'parada', 'implantacao', 'cancelada'];
const RISK_OPTIONS: RiskLevel[] = ['high', 'medium', 'low'];

const DEFAULT_PER_PAGE = 25;

const KpiCard = ({ label, value, format, sub, hint, delta = null, accent }: {
  label: string;
  /** null = o rollup ainda não tem esse número. Mostra traço, nunca zero. */
  value: number | null;
  format: (v: number) => string;
  sub?: React.ReactNode;
  hint?: string;
  /**
   * Variação vs a janela anterior. Hoje o rollup não publica delta no nível da
   * carteira (só por rede, na tabela), então fica null e o chip não renderiza —
   * é melhor não ter o número do que inventar um. Assim que `kpis` ganhar os
   * deltas, é só passar aqui.
   */
  delta?: number | null;
  accent?: string;
}) => {
  const animated = useCountUp(value ?? 0);
  return (
    <Box sx={{ ...cardSx, display: 'flex', flexDirection: 'column', minHeight: 108 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: COLORS.faint }}>
          {label}
        </Typography>
        {hint && (
          <Tooltip arrow title={hint}>
            <InfoOutlinedIcon sx={{ fontSize: 13, color: COLORS.faint }} />
          </Tooltip>
        )}
      </Box>
      <Typography sx={{ fontSize: 25, fontWeight: 800, color: accent ?? COLORS.ink, mt: 0.5 }}>
        {value === null ? '—' : format(animated)}
      </Typography>
      <Box sx={{ mt: 'auto', pt: 0.5 }}>
        <DeltaChip deltaPct={delta} />
        {sub && <Typography sx={{ fontSize: 11.5, color: COLORS.muted }}>{sub}</Typography>}
      </Box>
    </Box>
  );
};

const ProductEvolution = () => {
  const theme = useTheme();
  const { permissions, isLoading: permissionsLoading } = usePermissions();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const [windowDays, setWindowDays] = useState<PortfolioWindow>(30);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ChainLifecycleStatus | ''>('');
  const [riskLevel, setRiskLevel] = useState<RiskLevel | ''>('');
  const [orderBy, setOrderBy] = useState<PortfolioOrderBy>('churnScore');
  const [orderDir, setOrderDir] = useState<OrderDir>('desc');
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(DEFAULT_PER_PAGE);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => { track(EVENTS.DASHBOARD_OPENED, { dashboard: 'product-evolution' }); }, []);

  // Busca só vira request depois que o usuário para de digitar.
  useEffect(() => {
    const timer = setTimeout(() => { setSearch(searchInput.trim()); setPage(0); }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // O rollup só existe em janelas de 30/60/90 dias, mas o validator do backend
  // exige from/to — mandamos os dois derivados da janela, com `to` exclusivo.
  const filter = useMemo(() => ({
    windowDays,
    from: dayjs().subtract(windowDays, 'day').format('YYYY-MM-DD'),
    to: dayjs().add(1, 'day').format('YYYY-MM-DD'),
    orderBy,
    orderDir,
    // Só manda o que tem valor: `status: ''` não passa no Joi.valid do backend.
    ...(search ? { search } : {}),
    ...(status ? { status } : {}),
    ...(riskLevel ? { riskLevel } : {}),
  }), [windowDays, orderBy, orderDir, search, status, riskLevel]);

  const pagination = useMemo(() => ({ page: page + 1, perPage }), [page, perPage]);
  const portfolio = useDashboardSection<PortfolioData>('admin/metrics/portfolio', filter, true, pagination);

  const data = portfolio.data;
  const kpis = data?.kpis ?? null;
  const freshnessLabel = humanizeAgo(data?.computedAt);

  // Poll enquanto o backend declara `computing`. Dep é a flag booleana, NUNCA o
  // objeto `data` — com o objeto, cada resposta recria o intervalo em loop.
  const isComputing = data?.computing === true;
  const refetch = portfolio.refetch;
  useEffect(() => {
    if (!isComputing) return;
    const timer = setInterval(() => { refetch(); }, 20000);
    return () => clearInterval(timer);
  }, [isComputing, refetch]);

  // fillin_summary é D-1. Se o último dia agregado é anterior a ontem, o Lambda
  // agregador atrasou e os números estão velhos — melhor dizer do que esconder.
  const dataThrough = data?.dataThrough ?? null;
  const isStale = !!dataThrough && dayjs(dataThrough).isBefore(dayjs().subtract(1, 'day'), 'day');

  const handleSort = (key: PortfolioOrderBy) => {
    // Mesma coluna inverte a direção; coluna nova começa no que é mais útil
    // (nome em A→Z, número no maior primeiro).
    if (key === orderBy) setOrderDir(orderDir === 'desc' ? 'asc' : 'desc');
    else { setOrderBy(key); setOrderDir(key === 'name' ? 'asc' : 'desc'); }
    setPage(0);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      // Recompute é NÃO-bloqueante no backend: responde na hora com computing:true
      // e recalcula atrás. O refetch abaixo traz esse computing e liga o poll.
      await dataProvider.create('admin/metrics/refresh', { data: {} });
      await portfolio.refetch();
    } catch {
      notify('Não foi possível atualizar agora, tente de novo.', { type: 'warning' });
    } finally {
      setRefreshing(false);
    }
  };

  if (permissionsLoading) return null;

  const perms: string[] = permissions ?? [];
  if (!perms.includes(PERMISSIONS.ADMIN) || !perms.includes(PERMISSIONS.PRODUCT_EVOLUTION)) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <Typography color="text.secondary">Acesso não autorizado.</Typography>
      </Box>
    );
  }

  // Recharts herda a fonte do <body>, não a do tema — tem que ser passada à mão.
  const chartFont = theme.typography.fontFamily;
  const composition = kpis ? [{
    name: 'Carteira',
    ativas: kpis.activeChains,
    paradas: kpis.stalledChains,
    implantacao: kpis.implantationChains,
  }] : [];

  return (
    <Box sx={{ minHeight: '100vh', mx: -1, pb: 8, background: 'linear-gradient(145deg, #f0f4ff 0%, #f8fafc 45%, #f5f0ff 100%)' }}>
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 30, mb: 1,
        backdropFilter: 'blur(14px)', background: 'rgba(248,250,252,0.85)',
        borderBottom: '1px solid rgba(120,105,223,0.10)',
      }}>
        <Box sx={{
          maxWidth: 1400, mx: 'auto', px: { xs: 2.5, md: 3 }, py: 2,
          display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5,
        }}>
          <Box>
            <Typography sx={{
              fontSize: 26, fontWeight: 800,
              background: `linear-gradient(90deg, ${COLORS.brand2}, ${COLORS.brand}, #275030)`,
              WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>
              Evolução do Produto
            </Typography>
            <Typography sx={{ fontSize: 12.5, color: COLORS.muted }}>
              Carteira de redes ranqueada por risco · últimos {windowDays} dias comparados com os {windowDays} anteriores
            </Typography>
            {isComputing ? (
              <Typography sx={{ fontSize: 11.5, color: COLORS.brand, fontWeight: 600, mt: 0.25, display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
                <CircularProgress size={12} sx={{ color: COLORS.brand }} />
                Recalculando a carteira… os números aparecem assim que terminar.
              </Typography>
            ) : freshnessLabel ? (
              <Typography sx={{ fontSize: 11.5, color: COLORS.faint, mt: 0.25 }}>
                Carteira atualizada {freshnessLabel}
                {dataThrough ? ` · dados até ${dayjs(dataThrough).format('DD/MM/YYYY')}` : ''}
              </Typography>
            ) : portfolio.loading ? (
              <Typography sx={{ fontSize: 11.5, color: COLORS.faint, mt: 0.25 }}>Carregando a carteira…</Typography>
            ) : null}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
            {PORTFOLIO_WINDOWS.map(w => (
              <Chip
                key={w}
                label={`${w}d`}
                onClick={() => { setWindowDays(w); setPage(0); }}
                sx={windowDays === w ? {
                  fontWeight: 700, color: '#fff',
                  background: `linear-gradient(135deg, ${COLORS.brand2}, ${COLORS.brand})`,
                } : { fontWeight: 600, bgcolor: '#fff' }}
              />
            ))}
            <TextField
              size="small" placeholder="Buscar rede…" value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              sx={{ width: 210, bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            <TextField
              select size="small" label="Status" value={status}
              onChange={e => { setStatus(e.target.value as ChainLifecycleStatus | ''); setPage(0); }}
              sx={{ width: 160, bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <MenuItem value="">Todos</MenuItem>
              {STATUS_OPTIONS.map(s => <MenuItem key={s} value={s}>{STATUS_LABEL[s]}</MenuItem>)}
            </TextField>
            <TextField
              select size="small" label="Risco" value={riskLevel}
              onChange={e => { setRiskLevel(e.target.value as RiskLevel | ''); setPage(0); }}
              sx={{ width: 140, bgcolor: '#fff', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            >
              <MenuItem value="">Todos</MenuItem>
              {RISK_OPTIONS.map(r => <MenuItem key={r} value={r}>{RISK_LABEL[r]}</MenuItem>)}
            </TextField>
            <Button
              size="small" variant="outlined" onClick={handleRefresh} disabled={refreshing}
              startIcon={refreshing ? <CircularProgress size={15} sx={{ color: COLORS.brand }} /> : <RefreshIcon fontSize="small" />}
              sx={{
                textTransform: 'none', fontWeight: 700, borderRadius: 2, bgcolor: '#fff',
                borderColor: 'rgba(120,105,223,0.35)', color: COLORS.brand,
                '&:hover': { borderColor: COLORS.brand, bgcolor: 'rgba(120,105,223,0.06)' },
              }}
            >
              {refreshing ? 'Atualizando…' : 'Atualizar'}
            </Button>
          </Box>
        </Box>
      </Box>

      <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 2.5, md: 3 } }}>
        {isStale && (
          <Box sx={{
            mt: 1.5, px: 2, py: 1.25, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 1,
            bgcolor: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.30)',
          }}>
            <WarningAmberIcon sx={{ fontSize: 18, color: COLORS.warning }} />
            <Typography sx={{ fontSize: 12.5, color: COLORS.ink }}>
              Os dados de volume vão até <b>{dayjs(dataThrough).format('DD/MM/YYYY')}</b> — a agregação diária está atrasada.
              Ticket, abastecimentos e receita podem estar defasados.
            </Typography>
          </Box>
        )}

        <Section index={0}>
          {/* Tríptico dos KPIs: skeleton só no primeiro load (sem `data` na mão);
              erro tem retry; sem dado nenhum, some em vez de mostrar zero falso. */}
          {portfolio.loading && !data ? (
            <SkeletonCard height={120} />
          ) : portfolio.error ? (
            <ErrorCard onRetry={portfolio.refetch} height={120} />
          ) : !data ? null : (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' }, gap: 1.75 }}>
              <KpiCard
                label="Redes ativas" value={kpis?.activeChains ?? null} format={v => fmtNum(v)} accent={COLORS.success}
                sub={kpis ? `${fmtNum(kpis.stalledChains)} paradas · ${fmtNum(kpis.implantationChains)} em implantação` : undefined}
                hint="Ativas inclui as classificadas como em risco. Paradas são redes sem nenhum abastecimento na janela."
              />
              <KpiCard
                label="Redes em risco alto" value={kpis?.highRiskChains ?? null} format={v => fmtNum(v)} accent={COLORS.danger}
                sub="score de risco 55 ou mais"
                hint="O score soma sinais de movimento, postos parados, base de clientes, financeiro, uso do produto, acesso ao admin e satisfação."
              />
              <KpiCard
                label="MRR da carteira" value={kpis?.mrr ?? null} format={fmtBrl}
                sub="soma do preço mensal dos postos ativos"
              />
              <KpiCard
                label="Receita não capturada" value={kpis?.uncapturedRevenue ?? null} format={fmtBrl} accent={COLORS.warning}
                sub={kpis ? `${fmtNum(kpis.uncapturedPlaces)} postos rodando sem fatura${kpis.uncapturedEstimatedPlaces ? ` · ${fmtNum(kpis.uncapturedEstimatedPlaces)} valorados pela mediana` : ''}` : undefined}
                hint="Postos com movimento relevante, fora da carência e sem fatura na competência. Quem não tem preço próprio entra valorado pela mediana da carteira."
              />
              <KpiCard
                label="Potencial de upsell" value={kpis?.upsellPotential ?? null} format={fmtBrl} accent={COLORS.purple}
                sub="soma dos módulos não contratados"
                hint="Potencial declarado — os preços de módulo não entram na cobrança real."
              />
              <KpiCard
                label="Ticket médio da carteira" value={kpis?.avgTicketMedian ?? null} format={fmtBrl}
                sub="mediana entre as redes com movimento"
                hint="Mediana, não média: uma rede gigante não puxa o número da carteira inteira."
              />
              <KpiCard
                label="Abastecimentos na janela" value={kpis?.totalFillins ?? null} format={v => fmtNum(v)} accent={COLORS.blue}
                sub={dataThrough ? `agregados até ${dayjs(dataThrough).format('DD/MM')}` : undefined}
              />
              <Box sx={{ ...cardSx, minHeight: 108 }}>
                <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.8, textTransform: 'uppercase', color: COLORS.faint }}>
                  Composição da carteira
                </Typography>
                {!composition.length ? (
                  <Typography sx={{ fontSize: 12, color: COLORS.faint, mt: 2 }}>—</Typography>
                ) : (
                  <Box sx={{ height: 72, mt: 0.5 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={composition} layout="vertical" margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                        <XAxis type="number" hide tick={{ fontFamily: chartFont, fontSize: 11, fill: COLORS.muted }} />
                        <YAxis type="category" dataKey="name" hide tick={{ fontFamily: chartFont, fontSize: 11, fill: COLORS.muted }} />
                        <ReTooltip
                          cursor={{ fill: 'rgba(120,105,223,0.06)' }}
                          contentStyle={{ fontFamily: chartFont, fontSize: 12, borderRadius: 8, border: '1px solid rgba(120,105,223,0.2)' }}
                          formatter={(value: any, name: any) => [fmtNum(Number(value)), name]}
                        />
                        <Legend wrapperStyle={{ fontFamily: chartFont, fontSize: 11 }} iconSize={8} />
                        <Bar dataKey="ativas" name="Ativas" stackId="carteira" fill={COLORS.success} radius={[4, 0, 0, 4]} />
                        <Bar dataKey="paradas" name="Paradas" stackId="carteira" fill={COLORS.warning} />
                        <Bar dataKey="implantacao" name="Em implantação" stackId="carteira" fill={COLORS.blue} radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                )}
              </Box>
            </Box>
          )}
        </Section>

        <Section index={1}>
          <PortfolioView
            state={portfolio}
            orderBy={orderBy}
            orderDir={orderDir}
            onSort={handleSort}
            page={page}
            perPage={perPage}
            onPageChange={setPage}
            onPerPageChange={next => { setPerPage(next); setPage(0); }}
          />
        </Section>
      </Box>
    </Box>
  );
};

export default ProductEvolution;
